import { supabase } from '@/integrations/supabase/client';
import { ContactWithRelations, ContactFormData } from '@/types/contact.types';
import { logError } from '@/lib/error-handler';

export const fetchContacts = async () => {
  try {
    const { data, error } = await supabase
      .from('contacts')
      .select(
        `
        *,
        organization:organizations!contacts_organization_id_fkey(id, name)
      `
      )
      .order('last_name');

    if (error) {
      logError('Error fetching contacts:', error);
      
      // If it's an auth error, return empty array instead of throwing
      if (error.code === 'PGRST116' || error.message.includes('JWT')) {
        console.warn('Authentication required to access contacts');
        return [];
      }
      
      throw error;
    }
    
    console.log('Contacts fetched successfully:', data?.length || 0);
    return (data || []) as ContactWithRelations[];
  } catch (error) {
    logError('Critical error in fetchContacts:', error);
    throw error;
  }
};

export const fetchContactById = async (id: string) => {
  const { data, error } = await supabase
    .from('contacts')
    .select(
      `
      *,
      organization:organizations!contacts_organization_id_fkey(id, name)
    `
    )
    .eq('id', id)
    .maybeSingle();

  if (error) {
    logError('Error fetching contact by ID:', error);
    throw error;
  }

  if (!data) {
    throw new Error('Contact not found');
  }

  return data as ContactWithRelations;
};

export const createContact = async (contactData: ContactFormData) => {
  const { data, error } = await supabase
    .from('contacts')
    .insert(contactData)
    .select(
      `
      *,
      organization:organizations!contacts_organization_id_fkey(id, name)
    `
    )
    .single();

  if (error) throw error;

  // Trigger n8n workflow for new contact
  if (data) {
    const { n8nService } = await import('../automation/n8nService');
    await n8nService.onContactCreated(data);
  }

  return data;
};

export const updateContact = async (id: string, contactData: Partial<ContactFormData>) => {
  const { data, error } = await supabase
    .from('contacts')
    .update(contactData)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const deleteContactSafely = async (id: string) => {
  // Use the database function that checks dependencies
  const { data, error } = await supabase.rpc('delete_contact_safely', {
    contact_id: id,
  });

  if (error) throw error;

  if (!data.success) {
    throw new Error(data.message);
  }

  return data;
};

export const mergeContacts = async (sourceId: string, targetId: string) => {
  // Verify both contacts exist
  const { data: sourceContact, error: sourceError } = await supabase
    .from('contacts')
    .select('first_name, last_name')
    .eq('id', sourceId)
    .single();

  if (sourceError) throw sourceError;

  const { data: targetContact, error: targetError } = await supabase
    .from('contacts')
    .select('first_name, last_name')
    .eq('id', targetId)
    .single();

  if (targetError) throw targetError;

  if (!sourceContact || !targetContact) {
    throw new Error('Both source and target contacts must exist');
  }

  // Use the database function for merging contacts
  const { data, error: mergeError } = await supabase.rpc('merge_contacts', {
    source_id: sourceId,
    target_id: targetId,
  });

  if (mergeError) throw mergeError;

  return data;
};

export const fetchContactsByOrganization = async (organizationId: string) => {
  const { data, error } = await supabase
    .from('contacts')
    .select('*')
    .eq('organization_id', organizationId)
    .order('last_name');

  if (error) throw error;
  return data;
};
